import RPMDateTimePickerWithPlaceholder from '@/components/RPMDateTimePicker/RPMDateTimePickerWithPlaceholder';
import StyledModal from '@/components/StyledModal';
import { DAY, WEEKDAYS } from '@/constants/calendar';
import { CustomRecurrenceData } from '@/gql/events/types';
import { getRepeatEveryFrequency } from '@/utils/events';
import rem from '@/utils/rem';
import {
  Box,
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  ModalBody,
  ModalFooter,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
  Flex,
} from '@chakra-ui/react';
import { getColorWithOpacityFromToken } from '@/utils/color';
import { startOfDay } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconChevronDown } from '@/theme/icons';
import { Icon as ChakraIcon } from '@chakra-ui/react';

export type RecurrenceFrequencyApi = 'daily' | 'weekly' | 'monthly' | 'yearly';

interface CustomRecurrenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CustomRecurrenceData) => void;
  recurrence?: string;
  initialFrequency?: RecurrenceFrequencyApi;
  interval?: number;
  occurrences?: number;
  byDays?: { day: string }[];
  currentDate?: Date;
  /** Saved "Ends on" date when Ends = "On Date"; used to restore state when reopening */
  endDate?: Date | null;
}

const FREQUENCY_OPTIONS = ['Daily', 'Weekly', 'Monthly', 'Yearly'] as const;
const ENDS_OPTIONS = ['Never', 'On Date', 'After'] as const;

const frequencyToUnitKey = (freq: string, interval: number): string => {
  const map: Record<string, string> = {
    Daily: interval > 1 ? 'days' : 'day',
    Weekly: interval > 1 ? 'weeks' : 'week',
    Monthly: interval > 1 ? 'months' : 'month',
    Yearly: interval > 1 ? 'years' : 'year',
  };
  return map[freq] ?? 'week';
};

const apiFrequencyToTab: Record<RecurrenceFrequencyApi, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly',
};

/** Monday-first order for display (product setting: week starts on Monday) */
const WEEKDAYS_MONDAY_FIRST = [...WEEKDAYS.slice(1), WEEKDAYS[0]];

/** Fixed width for Ends dropdown and menu so they align to the same grid and match width */
const ENDS_FIELD_WIDTH = rem(200);

const CustomRecurrenceModal = ({
  isOpen,
  onClose,
  onSave,
  recurrence,
  initialFrequency,
  interval,
  occurrences,
  byDays,
  currentDate,
  endDate: savedEndDate,
}: CustomRecurrenceModalProps) => {
  const { t, i18n } = useTranslation('RecurrenceMenu');
  const fallbackFreq = getRepeatEveryFrequency(recurrence || 'week', interval || 1)?.toLowerCase();
  const [selectedRecurrence, setSelectedRecurrence] = useState<string>(() => {
    if (initialFrequency) return apiFrequencyToTab[initialFrequency];
    if (fallbackFreq === 'day' || fallbackFreq === 'days') return 'Daily';
    if (fallbackFreq === 'week' || fallbackFreq === 'weeks') return 'Weekly';
    if (fallbackFreq === 'month' || fallbackFreq === 'months') return 'Monthly';
    if (fallbackFreq === 'year' || fallbackFreq === 'years') return 'Yearly';
    return 'Weekly';
  });
  const [endsValue, setEndsValue] = useState<'Never' | 'On Date' | 'After'>('Never');
  const endsNeverRef = useRef<HTMLButtonElement>(null);
  const endsOnDateRef = useRef<HTMLButtonElement>(null);
  const endsAfterRef = useRef<HTMLButtonElement>(null);
  const endsOptionRefs = { Never: endsNeverRef, 'On Date': endsOnDateRef, After: endsAfterRef };
  const [scheduledDate, setScheduledDate] = useState<Date | null>(currentDate || null);
  const [selectedDays, setSelectedDays] = useState<string[]>(
    byDays?.length ? byDays.map((d) => d.day) : currentDate ? [WEEKDAYS[currentDate.getDay()]] : [],
  );
  const [selectedInterval, setSelectedInterval] = useState<string>(String(interval || 1));
  const [selectedNumberOfOccurrences, setSelectedNumberOfOccurrences] = useState<string>(String(occurrences ?? 2));
  const everyInputRef = useRef<HTMLInputElement>(null);
  const afterInputRef = useRef<HTMLInputElement>(null);

  const {
    isOpen: isDatePickerOpen,
    onOpen: onDatePickerOpen,
    onClose: onDatePickerClose,
  } = useDisclosure({ id: 'datepicker-repeat-ends' });

  const recurrenceActiveBg = useColorModeValue('cyan.200', 'blue.950');
  const recurrenceSelectedHoverBg = useColorModeValue('cyan.100', 'blue.900');
  const recurrenceUnselectedHoverBg = useColorModeValue(
    'cyan.100',
    getColorWithOpacityFromToken('blue.950', '0.6'),
  );
  const endsMenuListBg = useColorModeValue('background-primary', 'gray.700');
  const endsMenuItemIdleBg = useColorModeValue('background-primary', 'background-tertiary');

  const weekdaySelectedBg = useColorModeValue('cyan.200', 'white');
  const weekdaySelectedHoverBg = useColorModeValue('cyan.100', 'gray.100');
  const weekdaySelectedBorderColor = useColorModeValue('button-bg-primary', 'transparent');
  const weekdaySelectedTextColor = useColorModeValue('text-primary', 'gray.800');

  useEffect(() => {
    if (isOpen) {
      if (initialFrequency) {
        setSelectedRecurrence(apiFrequencyToTab[initialFrequency]);
      } else {
        const r = getRepeatEveryFrequency(recurrence || 'week', interval || 1)?.toLowerCase();
        if (r === 'day' || r === 'days') setSelectedRecurrence('Daily');
        else if (r === 'week' || r === 'weeks') setSelectedRecurrence('Weekly');
        else if (r === 'month' || r === 'months') setSelectedRecurrence('Monthly');
        else if (r === 'year' || r === 'years') setSelectedRecurrence('Yearly');
      }
      setSelectedInterval(String(interval || 1));
      setSelectedNumberOfOccurrences(String(occurrences ?? 2));
      setSelectedDays(byDays?.length ? byDays.map((d) => d.day) : currentDate ? [WEEKDAYS[currentDate.getDay()]] : []);

      if (savedEndDate) {
        setEndsValue('On Date');
        setScheduledDate(savedEndDate);
      } else if (occurrences != null && occurrences > 0) {
        setEndsValue('After');
        setScheduledDate(currentDate || null);
      } else {
        setEndsValue('Never');
        setScheduledDate(currentDate || null);
      }
    }
  }, [isOpen, initialFrequency, recurrence, interval, occurrences, byDays, currentDate, savedEndDate]);

  const handleScheduledDate = useCallback((date: Date) => {
    setScheduledDate(date);
  }, []);

  const onClickUnplan = useCallback(() => {
    setScheduledDate(null);
    onDatePickerClose();
  }, [onDatePickerClose]);

  const onDatePickerSave = useCallback(() => {
    onDatePickerClose();
  }, [onDatePickerClose]);

  const toggleDaySelection = (day: string) => {
    setSelectedDays((prevSelectedDays) => {
      const defaultDays = currentDate ? [WEEKDAYS[currentDate.getDay()]] : [];
      const isDefault =
        prevSelectedDays.length === defaultDays.length && prevSelectedDays.every((d) => defaultDays.includes(d));
      if (isDefault && defaultDays.length === 1 && defaultDays.includes(day)) return prevSelectedDays;
      if (prevSelectedDays.includes(day)) {
        const updated = prevSelectedDays.filter((d) => d !== day);
        return updated.length === 0 ? defaultDays : updated;
      }
      return [...prevSelectedDays, day];
    });
  };

  const frequencyToRule: Record<string, string> = {
    Daily: 'Day',
    Weekly: 'Week',
    Monthly: 'Month',
    Yearly: 'Year',
  };
  const selectedRecurrenceForRule = frequencyToRule[selectedRecurrence] ?? 'Week';

  const handleSubmit = () => {
    onSave({
      selectedWeekDays: selectedRecurrence === 'Weekly' ? selectedDays.map((day) => ({ day })) : [],
      interval: Math.max(1, +selectedInterval || 1),
      selectedRecurrence: selectedRecurrenceForRule,
      occurrences: endsValue === 'After' ? Math.max(1, +selectedNumberOfOccurrences || 1) : null,
      ...(endsValue === 'On Date' && scheduledDate && { scheduledDate }),
    });
  };

  const endsLabel =
    endsValue === 'Never'
      ? t('labels.never')
      : endsValue === 'On Date'
        ? t('labels.onDate')
        : t('labels.after');

  return (
    <StyledModal isOpen={isOpen} onClose={onClose} title={t('repeatModal.title')} size="md">
      <ModalBody minHeight={rem(80)} paddingTop={rem(8)}>
        <VStack alignItems="stretch" gap={rem(28)}>
          <Flex
            overflow="hidden"
            width="full"
            border={`${rem(1)} solid`}
            borderColor="stroke-primary"
            borderRadius={rem(6)}
            backgroundColor="background-primary"
          >
            {FREQUENCY_OPTIONS.map((opt, index) => (
              <Button
                key={opt}
                flex={1}
                color="text-primary"
                borderRightWidth={index < FREQUENCY_OPTIONS.length - 1 ? rem(1) : 0}
                borderRightColor="stroke-primary"
                borderRadius="none"
                _hover={{
                  backgroundColor:
                    selectedRecurrence === opt ? recurrenceSelectedHoverBg : recurrenceUnselectedHoverBg,
                }}
                backgroundColor={selectedRecurrence === opt ? recurrenceActiveBg : 'transparent'}
                onClick={() => setSelectedRecurrence(opt)}
                size="sm"
              >
                {t(`labels.${opt.toLowerCase()}`)}
              </Button>
            ))}
          </Flex>
          <HStack alignItems="center" gap={rem(8)}>
            <Text flex="0" minWidth={rem(40)} fontWeight="normal" lineHeight="1">
              {t('labels.every')}
            </Text>
            <Flex
              alignItems="center"
              width="fit-content"
              cursor="text"
              onClick={() => everyInputRef.current?.focus()}
            >
              <NumberInput
                max={99}
                min={1}
                onChange={(_, val) => setSelectedInterval(String(val || 1))}
                step={1}
                value={selectedInterval}
              >
                <NumberInputField
                  ref={everyInputRef}
                  width={rem(48)}
                  height={rem(32)}
                  padding={0}
                  textAlign="center"
                  border={`${rem(1)} solid`}
                  borderColor="stroke-primary"
                  borderRadius={rem(4)}
                  _hover={{ boxShadow: 'none' }}
                  _focusVisible={{ borderColor: 'gray.400' }}
                  backgroundColor="button-bg-secondary"
                />
                <NumberInputStepper />
              </NumberInput>
            </Flex>
            <Text lineHeight="1">{t(`labels.${frequencyToUnitKey(selectedRecurrence, +selectedInterval || 1)}`)}</Text>
          </HStack>
          {selectedRecurrence === 'Weekly' && (
            <HStack alignItems="center" gap={rem(8)}>
              <Text flex="0" minWidth={rem(40)} fontWeight="normal" lineHeight="1">
                {t('labels.on')}
              </Text>
              <HStack gap={rem(8)}>
                {WEEKDAYS_MONDAY_FIRST.map((day) => {
                  const isSelected = selectedDays.includes(day);
                  return (
                    <Button
                      key={day}
                      width={rem(34)}
                      minWidth={rem(34)}
                      height={rem(34)}
                      color={isSelected ? weekdaySelectedTextColor : 'text-primary'}
                      fontWeight={500}
                      textTransform="uppercase"
                      border={`${rem(2)} solid`}
                      borderColor={isSelected ? weekdaySelectedBorderColor : 'stroke-primary'}
                      borderRadius="50%"
                      _hover={{
                        backgroundColor: isSelected ? weekdaySelectedHoverBg : recurrenceUnselectedHoverBg,
                        borderColor: isSelected ? weekdaySelectedBorderColor : 'transparent',
                      }}
                      backgroundColor={isSelected ? weekdaySelectedBg : 'button-bg-secondary'}
                      onClick={() => toggleDaySelection(day)}
                      size="sm"
                    >
                      {day.slice(0, 1)}
                    </Button>
                  );
                })}
              </HStack>
            </HStack>
          )}
          <HStack alignItems="center" gap={rem(8)}>
            <Text flex="0" minWidth={rem(40)} fontWeight="normal" lineHeight="1">
              {t('labels.ends')}
            </Text>
            <Box flexShrink={0} width={ENDS_FIELD_WIDTH}>
              <Menu
                closeOnSelect={true}
                initialFocusRef={endsOptionRefs[endsValue]}
                placement="bottom-start"
              >
                <MenuButton
                  as={Button}
                  justifyContent="space-between"
                  width="100%"
                  height={rem(36)}
                  color="text-primary"
                  textAlign="left"
                  border={`${rem(1)} solid`}
                  borderColor="stroke-primary"
                  borderRadius={rem(4)}
                  _hover={{
                    backgroundColor: 'background-quaternary',
                  }}
                  _active={{
                    backgroundColor: 'background-secondary',
                  }}
                  backgroundColor="background-primary"
                  paddingX={rem(8)}
                  rightIcon={<ChakraIcon as={IconChevronDown} color="currentColor" />}
                  size="sm"
                  variant="outline"
                >
                  {endsLabel}
                </MenuButton>
                <MenuList
                  zIndex={5}
                  width={ENDS_FIELD_WIDTH}
                  minWidth={ENDS_FIELD_WIDTH}
                  padding={0}
                  borderWidth={1}
                  borderColor="stroke-primary"
                  borderRadius={4}
                  boxShadow="0px 0px 10px 0px var(--chakra-colors-black-900a06)"
                  backgroundColor={endsMenuListBg}
                >
                  {ENDS_OPTIONS.map((opt) => (
                    <MenuItem
                      key={opt}
                      ref={endsOptionRefs[opt]}
                      as={Button}
                      sx={{
                        '&:hover, &:focus': {
                          backgroundColor:
                            endsValue === opt ? recurrenceSelectedHoverBg : recurrenceUnselectedHoverBg,
                        },
                      }}
                      justifyContent="flex-start"
                      color="text-primary"
                      fontWeight="normal"
                      textTransform="initial"
                      borderRadius={0}
                      backgroundColor={endsValue === opt ? recurrenceActiveBg : endsMenuItemIdleBg}
                      onClick={() => setEndsValue(opt as 'Never' | 'On Date' | 'After')}
                    >
                      {opt === 'Never' ? t('labels.never') : opt === 'On Date' ? t('labels.onDate') : t('labels.after')}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </Box>
          </HStack>
          {endsValue === 'On Date' && (
            <HStack alignItems="center" gap={rem(8)} width="full">
              <Text flex="0 0 auto" minWidth={rem(72)} fontWeight="normal" lineHeight="1" whiteSpace="nowrap">
                {t('labels.endDate')}
              </Text>
              <Box flexShrink={0} width={ENDS_FIELD_WIDTH}>
                <RPMDateTimePickerWithPlaceholder
                  onDatePickerOpen={onDatePickerOpen}
                  isDatePickerOpen={isDatePickerOpen}
                  onSave={onDatePickerSave}
                  onCancel={onClickUnplan}
                  placeholder={t('labels.chooseDate')}
                  onClickUnplan={onClickUnplan}
                  scheduledDate={scheduledDate}
                  tabIndex={DAY}
                  updateSelectedDate={handleScheduledDate}
                  customWidth={ENDS_FIELD_WIDTH}
                  border={`${rem(1)} solid`}
                  borderColor="stroke-primary"
                  height={rem(36)}
                  datePickerProps={
                    { minDate: startOfDay(currentDate || new Date()) } as React.ComponentProps<
                      typeof RPMDateTimePickerWithPlaceholder
                    >['datePickerProps']
                  }
                />
              </Box>
            </HStack>
          )}
          {endsValue === 'After' && (
            <HStack alignItems="center" gap={rem(8)}>
              <Text flex="0" minWidth={rem(40)} fontWeight="normal" lineHeight="1">
                {t('labels.after')}
              </Text>
              <Flex
                alignItems="center"
                width="fit-content"
                cursor="text"
                onClick={() => afterInputRef.current?.focus()}
              >
                <NumberInput
                  max={99}
                  min={1}
                  onChange={(_, val) => setSelectedNumberOfOccurrences(String(val || 1))}
                  step={1}
                  value={selectedNumberOfOccurrences}
                >
                  <NumberInputField
                    ref={afterInputRef}
                    width={rem(48)}
                    height={rem(32)}
                    padding={0}
                    textAlign="center"
                    border={`${rem(1)} solid`}
                    borderColor="stroke-primary"
                    borderRadius={rem(4)}
                    _hover={{ boxShadow: 'none' }}
                    _focusVisible={{ borderColor: 'gray.400' }}
                    backgroundColor="button-bg-secondary"
                  />
                  <NumberInputStepper />
                </NumberInput>
              </Flex>
              <Text lineHeight="1">{t('labels.times')}</Text>
            </HStack>
          )}
        </VStack>
      </ModalBody>
      <ModalFooter justifyContent="flex-end" gap={rem(16)} display="flex">
        <HStack gap={rem(16)}>
          <Button onClick={onClose} size="md" variant="secondary">
            {t('labels.cancel')}
          </Button>
          <Button onClick={handleSubmit} size="md" type="button" variant="primary">
            {t('labels.done')}
          </Button>
        </HStack>
      </ModalFooter>
    </StyledModal>
  );
};

export default CustomRecurrenceModal;
